import { useEffect } from 'react';
import { useUnit } from 'effector-react';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
  useTheme,
  Typography,
} from '@mui/material';
import { Listbox } from '../../components/listbox';
import { withControl } from '../../hoc/withControl';
import { searchResidentsQuery, searchResidents, resetData } from './model';
import * as Styled from './styled';
import type { ControlProps } from './types';

export const ResidentsListSearchControl = withControl<ControlProps>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    isError,
    residentType = [],
  }) => {
    const theme = useTheme();
    const { data, pending } = useUnit(searchResidentsQuery);

    useEffect(() => () => resetData(), []);

    const handleInputChange = (_: unknown, value: string, reason: string) => {
      if (reason === 'input') {
        searchResidents({ value, residentType });
        setValue(value);

        return;
      }

      if (reason === 'blur' || reason === 'clear') {
        resetData();
        setValue('');

        return;
      }

      if (reason === 'selectOption') {
        setValue(value);
        resetData();

        return;
      }

      return;
    };

    return (
      <FormControl required={required} disabled={disabled}>
        {label && <FormLabel error={isError}>{label}</FormLabel>}
        <Autocomplete
          freeSolo
          loading={pending}
          value={value === '' ? null : value}
          options={Array.isArray(data) ? data : []}
          onInputChange={handleInputChange}
          ListboxComponent={Listbox}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                readOnly,
                error: isError,
                endAdornment: readOnly ? null : (
                  <>
                    {pending && <CircularProgress size={20} color="inherit" />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={({ key, ...optionProps }, option, { inputValue }) => {
            const matches = match(option.label, inputValue, { insideWords: true });
            const parts = parse(option.label, matches);

            return (
              <li key={key} {...optionProps}>
                <Styled.Option>
                  <div>
                    {parts.map(
                      (part: { text: string; highlight: boolean }, index: number) => (
                        <span
                          key={index}
                          style={{
                            color: part.highlight
                              ? theme.palette.primary.main
                              : theme.palette.text.primary,
                          }}
                        >
                          {part.text}
                        </span>
                      )
                    )}
                  </div>
                  {(option.phone || option.email) && (
                    <Typography variant="body2" color="textSecondary">
                      {`${option.phone ? `${option.phone}, ` : ''}${option.email}`}
                    </Typography>
                  )}
                </Styled.Option>
              </li>
            );
          }}
          readOnly={readOnly}
          disabled={disabled}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  }
);
